/**
 * TrainingPage + TrainingRoutes — URL-routed training viewer for Sentinel portal
 *
 * Routes (nested under /training/*):
 *   /training/courses         — course list
 *   /training/course?id=X     — single course
 *   /training/module?id=X     — module viewer
 *   /training/assessment?id=X — assessment
 */

import { Routes, Route, Navigate, useSearchParams, useNavigate } from 'react-router-dom';
import { CourseList, CourseView, ModuleView, AssessmentView } from '../../components/training/viewer';
import { useAuth } from '../../contexts/AuthContext';
import { POS_GOLD } from '../../config/posRoomStatus';

const VIOLET = '#772cbb';

/**
 * Pick the accent colour for the viewer's inner components (progress bar,
 * buttons, back link) based on the ?brand= URL param. The Charter Suite
 * share panel emits links with brand=pos; everything else stays violet.
 */
function useAccentColor(): string {
  const [searchParams] = useSearchParams();
  return searchParams.get('brand') === 'pos' ? POS_GOLD : VIOLET;
}

/**
 * When navigating between sub-views inside the training viewer, preserve
 * the brand flag so the skin doesn't reset on every click.
 */
function useBrandSuffix(): string {
  const [searchParams] = useSearchParams();
  return searchParams.get('brand') === 'pos' ? '&brand=pos' : '';
}

function CoursesPage() {
  const navigate = useNavigate();
  const accentColor = useAccentColor();
  return (
    <CourseList
      onSelectCourse={(id) => navigate(`/training/course?id=${id}`)}
      accentColor={accentColor}
    />
  );
}

function CoursePage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const accentColor = useAccentColor();
  const brandSuffix = useBrandSuffix();
  const id = searchParams.get('id') || '';

  if (!id) return <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--color-text-light)' }}>No course ID provided.</div>;

  // Anonymous visitors arriving via a share link have no meaningful
  // "All Courses" destination (the list is empty for them by design).
  // Hide the back affordance entirely.
  const showBack = Boolean(user);

  return (
    <CourseView
      courseId={id}
      onSelectModule={(mId) => navigate(`/training/module?id=${mId}&courseId=${id}${brandSuffix}`)}
      onSelectAssessment={(aId) => navigate(`/training/assessment?id=${aId}&courseId=${id}${brandSuffix}`)}
      onBack={() => navigate(`/training/courses${brandSuffix ? `?${brandSuffix.slice(1)}` : ''}`)}
      showBack={showBack}
      accentColor={accentColor}
    />
  );
}

function ModulePage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const accentColor = useAccentColor();
  const brandSuffix = useBrandSuffix();
  const id = searchParams.get('id') || '';
  const courseId = searchParams.get('courseId') || '';

  if (!id) return <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--color-text-light)' }}>No module ID provided.</div>;

  // Show the back affordance when the visitor has a useful destination:
  // authenticated operators always; anonymous visitors only if they
  // arrived from a course page (courseId in URL).
  const showBack = Boolean(user) || Boolean(courseId);

  return (
    <ModuleView
      moduleId={id}
      onBack={() => courseId
        ? navigate(`/training/course?id=${courseId}${brandSuffix}`)
        : navigate(`/training/courses${brandSuffix ? `?${brandSuffix.slice(1)}` : ''}`)}
      showBack={showBack}
      accentColor={accentColor}
    />
  );
}

function AssessmentPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const accentColor = useAccentColor();
  const brandSuffix = useBrandSuffix();
  const id = searchParams.get('id') || '';
  const courseId = searchParams.get('courseId') || '';

  if (!id) return <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--color-text-light)' }}>No assessment ID provided.</div>;

  return (
    <AssessmentView
      assessmentId={id}
      onBack={() => courseId
        ? navigate(`/training/course?id=${courseId}${brandSuffix}`)
        : navigate(`/training/courses${brandSuffix ? `?${brandSuffix.slice(1)}` : ''}`)}
      accentColor={accentColor}
    />
  );
}

export function TrainingRoutes() {
  return (
    <Routes>
      <Route path="courses" element={<CoursesPage />} />
      <Route path="course" element={<CoursePage />} />
      <Route path="module" element={<ModulePage />} />
      <Route path="assessment" element={<AssessmentPage />} />
      <Route path="*" element={<Navigate to="/training/courses" replace />} />
    </Routes>
  );
}
